import React, { useState, useContext, useEffect } from 'react';
import { UserContext } from '../contexts/UserContext';
import { buildAuthHeaders } from '../utils/auth';
import styles from './FlashAdd.module.css';
import heic2any from 'heic2any';
// imageCompression - 현재 원본 파일 직접 사용으로 비활성화
// import imageCompression from 'browser-image-compression';

// 언어 감지 및 텍스트
const getLang = () => (navigator.language || navigator.userLanguage || '').toLowerCase().startsWith('ko') ? 'ko' : 'en';
const TEXT = {
  title: { ko: '플래시 등록', en: 'Add Flash' },
  flashImage: { ko: '플래시 이미지', en: 'Flash Image' },
  imageFormat: { ko: 'JPG, PNG, WEBP만 가능. (이미지는 미리보기로 축소 표시)', en: 'JPG, PNG, WEBP only. (Image is displayed as preview)' },
  flashTitle: { ko: '플래시 제목 (빈칸 가능)', en: 'Flash title (optional)' },
  category: { ko: '카테고리', en: 'Category' },
  noCategory: { ko: '카테고리없음', en: 'No Category' },
  loading: { ko: '불러오는 중...', en: 'Loading...' },
  noCategorySelected: { ko: '선택된 카테고리 없음', en: 'No category selected' },
  noCategories: { ko: '카테고리가 없습니다.', en: 'No categories available.' },
  flashDescription: { ko: '플래시 설명 (빈칸 가능)', en: 'Flash description (optional)' },
  editable: { ko: '수정요청 가능여부', en: 'Modification Request' },
  duplicable: { ko: '중복타투 가능여부', en: 'Duplicate Tattoo' },
  yes: { ko: '예', en: 'Yes' },
  no: { ko: '아니오', en: 'No' },
  minPrice: { ko: '최소가격 (유로)', en: 'Min price (EURO)' },
  maxPrice: { ko: '최대가격 (유로)', en: 'Max price (EURO)' },
  priceNote: { ko: '가격을 입력하지 않으면 "가격 미정"으로 처리됩니다.', en: 'If no price is entered, it will be marked as "Price TBD".' },
  minSize: { ko: '최소사이즈 (cm)', en: 'Min size (cm)' },
  maxSize: { ko: '최대사이즈 (cm)', en: 'Max size (cm)' },
  sizeNote: { ko: '사이즈를 입력하지 않으면 "미정"으로 처리됩니다.', en: 'If no size is entered, it will be marked as "TBD".' },
  recommendPart: { ko: '추천 부위 (예: 팔, 다리, 등, 미정 가능)', en: 'Recommended area (e.g., arm, leg, back, TBD)' },
  forbidPart: { ko: '금지 부위 (예: 얼굴, 손, 미정 가능)', en: 'Forbidden area (e.g., face, hand, TBD)' },
  register: { ko: '플래시 등록', en: 'Add Flash' },
  registering: { ko: '등록 중...', en: 'Adding...' },
  selectImage: { ko: '이미지 파일을 선택하세요.', en: 'Please select an image file.' },
  success: { ko: '플래시 등록 성공!', en: 'Flash added successfully!' },
  failed: { ko: '등록 실패', en: 'Failed to add' },
  networkError: { ko: '네트워크 오류', en: 'Network error' },
  serverError: { ko: '서버 오류', en: 'Server error' },
  categoryError: { ko: '카테고리 목록을 불러오지 못했습니다.', en: 'Failed to load categories.' },
  imageTypeError: { ko: 'JPG, PNG, WEBP 파일만 업로드 가능합니다.', en: 'Only JPG, PNG, WEBP files can be uploaded.' }
};

function FlashAdd() {
  const { userInfo } = useContext(UserContext);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // 카테고리 상태
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState('');
  // 추가 필드
  const [editable, setEditable] = useState('yes');
  const [duplicable, setDuplicable] = useState('yes');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minSize, setMinSize] = useState('');
  const [maxSize, setMaxSize] = useState('');
  const [recommendPart, setRecommendPart] = useState('');
  const [forbidPart, setForbidPart] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const lang = getLang();

  // 카테고리 목록 fetch
  useEffect(() => {
    if (!userInfo?.user_id) return;
    setCatLoading(true);
    setCatError('');
    fetch(`/api/flash-categories/artist/${userInfo.user_id}`)
      .then(res => {
        if (!res.ok) throw new Error(TEXT.serverError[lang]);
        return res.json();
      })
      .then(data => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => setCatError(TEXT.categoryError[lang]))
      .finally(() => setCatLoading(false));
  }, [userInfo, lang]);

  // 이미지 업로드 및 미리보기
  const handleImageChange = async (e) => {
    let file = e.target.files[0];
    if (!file) return;
    // 1. heic → jpg 변환 시도
    if (file.type === 'image/heic' || file.name.endsWith('.heic')) {
      try {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
        file = new File([converted], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
      } catch (err) {
        alert(lang === 'ko' ? 'HEIC 파일 변환에 실패했습니다. iPhone 카메라 설정에서 "가장 호환성 높은 형식"으로 변경하거나, jpg/png/webp로 변환 후 업로드 해주세요.' : 'Failed to convert HEIC. Please set iPhone camera to "Most Compatible" or convert to jpg/png/webp before uploading.');
        return;
      }
    }
    // 2. 파일 크기 제한 (20MB 이하)
    if (file.size > 20 * 1024 * 1024) {
      alert(lang === 'ko' ? '이미지 용량은 20MB 이하만 가능합니다.' : 'Image size must be 20MB or less.');
      return;
    }
    // 3. 확장자 체크 (jpg, jpeg, png, webp만 허용)
    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert(TEXT.imageTypeError[lang]);
      return;
    }
    // 4. 압축 없이 원본 파일 사용
    setImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!image) {
      setMessage({ type: 'error', text: TEXT.selectImage[lang] });
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', image);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('categoryId', categoryId);
      formData.append('editable', editable);
      formData.append('duplicable', duplicable);
      formData.append('minPrice', minPrice);
      formData.append('maxPrice', maxPrice);
      formData.append('minSize', minSize);
      formData.append('maxSize', maxSize);
      formData.append('recommendPart', recommendPart);
      formData.append('forbidPart', forbidPart);
      const res = await fetch('/api/flashes', {
        method: 'POST',
        headers: buildAuthHeaders(),
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        setMessage({ type: 'success', text: TEXT.success[lang] });
        setImage(null); setPreview(null); setTitle(''); setDescription(''); setCategoryId(''); setEditable('yes'); setDuplicable('yes'); setMinPrice(''); setMaxPrice(''); setMinSize(''); setMaxSize(''); setRecommendPart(''); setForbidPart('');
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.message || TEXT.failed[lang] });
      }
    } catch (err) {
      setMessage({ type: 'error', text: TEXT.networkError[lang] });
    } finally {
      setLoading(false);
    }
  };

  // 자동 높이 조정 함수
  const adjustTextareaHeight = (element) => {
    element.style.height = 'auto';
    element.style.height = element.scrollHeight + 'px';
  };

  // Description 자동 높이 조정을 위한 useEffect
  useEffect(() => {
    const descriptionTextarea = document.querySelector(`.${styles.textarea}[data-field="description"]`);
    if (descriptionTextarea) {
      adjustTextareaHeight(descriptionTextarea);
    }
  }, [description]);

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>{TEXT.title[lang]}</h2>
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* 이미지 업로드 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>{TEXT.flashImage[lang]}</label>
          <input
            type="file"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleImageChange}
          />
          {preview && (
            <div className={styles.imagePreview}>
              <img
                src={preview}
                alt="preview"
                className={styles.previewImg}
              />
            </div>
          )}
          <div className={styles.infoText}>
            {TEXT.imageFormat[lang]}
          </div>
        </div>
        {/* 플래시 제목 */}
        <div className={styles.formGroup}>
          <input
            type="text"
            placeholder={TEXT.flashTitle[lang]}
            value={title}
            onChange={e => setTitle(e.target.value)}
            className={styles.input}
          />
        </div>
        {/* 카테고리 선택 */}
        <div className={styles.formGroup}>
          <label className={styles.label}>{TEXT.category[lang]}</label>
          <div className={styles.categoryDropdown}>
            <button
              type="button"
              onClick={() => setCatOpen(v => !v)}
              className={styles.categoryBtn}
              disabled={catLoading || categories.length === 0}
            >
              {catLoading ? TEXT.loading[lang] :
                (categoryId === '' ? TEXT.noCategory[lang] : (categories.find(c => c.id === Number(categoryId))?.name || TEXT.noCategorySelected[lang]))}
              <span style={{ float: 'right', color: '#888' }}>▼</span>
            </button>
            {catOpen && (
              <div className={styles.categoryList}>
                {/* '카테고리없음' 기본 항목 */}
                <div
                  key="none"
                  onClick={() => { setCategoryId(''); setCatOpen(false); }}
                  className={categoryId === '' ? `${styles.categoryItem} ${styles.selected} ${styles.noCategory}` : `${styles.categoryItem} ${styles.noCategory}`}
                >
                  {TEXT.noCategory[lang]}
                </div>
                {catError && <div className={styles.errorMsg}>{catError}</div>}
                {!catError && categories.length === 0 && <div className={styles.emptyMsg}>{TEXT.noCategories[lang]}</div>}
                {!catError && categories.map(cat => (
                  <div
                    key={cat.id}
                    onClick={() => { setCategoryId(cat.id.toString()); setCatOpen(false); }}
                    className={categoryId === cat.id.toString() ? `${styles.categoryItem} ${styles.selected}` : styles.categoryItem}
                  >
                    {cat.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* 플래시설명 */}
        <div className={styles.formGroup}>
          <textarea
            placeholder={TEXT.flashDescription[lang]}
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              adjustTextareaHeight(e.target);
            }}
            className={styles.textarea}
            data-field="description"
          />
        </div>
        {/* 수정요청가능여부 */}
        <div className={styles.formGroup}>
          <label className={styles.radioLabel}>{TEXT.editable[lang]}</label>
          <label><input type="radio" name="editable" value="yes" checked={editable === 'yes'} onChange={() => setEditable('yes')} /> {TEXT.yes[lang]}</label>
          <label className={styles.radioOption}><input type="radio" name="editable" value="no" checked={editable === 'no'} onChange={() => setEditable('no')} /> {TEXT.no[lang]}</label>
        </div>
        {/* 중복타투가능여부 */}
        <div className={styles.formGroup}>
          <label className={styles.radioLabel}>{TEXT.duplicable[lang]}</label>
          <label><input type="radio" name="duplicable" value="yes" checked={duplicable === 'yes'} onChange={() => setDuplicable('yes')} /> {TEXT.yes[lang]}</label>
          <label className={styles.radioOption}><input type="radio" name="duplicable" value="no" checked={duplicable === 'no'} onChange={() => setDuplicable('no')} /> {TEXT.no[lang]}</label>
        </div>
        {/* 최소/최대 가격 */}
        <div className={`${styles.formGroup} ${styles.flexRow}`}>
          <input
            type="number"
            placeholder={TEXT.minPrice[lang]}
            value={minPrice}
            onChange={e => setMinPrice(e.target.value)}
            className={styles.input}
            min={0}
          />
          <input
            type="number"
            placeholder={TEXT.maxPrice[lang]}
            value={maxPrice}
            onChange={e => setMaxPrice(e.target.value)}
            className={styles.input}
            min={0}
          />
        </div>
        <div className={styles.infoText}>
          {TEXT.priceNote[lang]}
        </div>
        {/* 최소/최대 사이즈 */}
        <div className={`${styles.formGroup} ${styles.flexRow}`}>
          <input
            type="number"
            placeholder={TEXT.minSize[lang]}
            value={minSize}
            onChange={e => setMinSize(e.target.value)}
            className={styles.input}
            min={0}
          />
          <input
            type="number"
            placeholder={TEXT.maxSize[lang]}
            value={maxSize}
            onChange={e => setMaxSize(e.target.value)}
            className={styles.input}
            min={0}
          />
        </div>
        <div className={styles.infoText}>
          {TEXT.sizeNote[lang]}
        </div>
        {/* 추천부위 */}
        <div className={styles.formGroup}>
          <input
            type="text"
            placeholder={TEXT.recommendPart[lang]}
            value={recommendPart}
            onChange={e => setRecommendPart(e.target.value)}
            className={styles.input}
          />
        </div>
        {/* 금지부위 */}
        <div className={styles.formGroup}>
          <input
            type="text"
            placeholder={TEXT.forbidPart[lang]}
            value={forbidPart}
            onChange={e => setForbidPart(e.target.value)}
            className={styles.input}
          />
        </div>
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={loading}
        >
          {loading ? TEXT.registering[lang] : TEXT.register[lang]}
        </button>
        {message && (
          <div className={`${styles.message} ${message.type === 'error' ? styles.error : styles.success}`}>
            {message.text}
          </div>
        )}
      </form>
    </div>
  );
}

export default FlashAdd; 
